import { useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import editGroupAccessConfigurationSchema from 'web-library/models/UserGroupAccessConfiguration/editGroupAccessConfigurationSchema';
import { FormEvent, useEffect, useState } from 'react';
import { UploadedFile } from 'web-library/models/UploadedFile';
import { MutationStatus } from 'react-query';
import {
  FunctionModule,
  UserGroupApproverRequest,
  UserGroupAuthorityRequest,
} from 'web-library/models/UserGroupAccessConfiguration/UserGroupAccessConfiguration';

import {
  ApiButton,
  Box,
  Button,
  ChangeRequestDialog,
  ChangeRequestForm,
  ContentTemplate,
  Divider,
  Grid,
  prepareFiles,
  TitleHeader,
  Typography,
} from 'web-library';
import UserGroupFunctionPermissionAccessOptionsDataTable from 'web-library/components/UserGroupFunctionPermissionAccessOptionsDataTable';
import TransactionAuditLogUpdatedDetails from 'web-library/components/TransactionAuditLogUpdatedDetails';

import { makeStyles } from 'tss-react/mui';
import { TFunction } from 'i18next';
import { ConfigurationType } from 'web-library/models/DropDown';
import { REQUEST_TYPES } from 'web-library/models/Request';
import TransactionLogModal from '../TransactionLogModal';
import { usePostUserGroupAccessMatrix } from '../../api-refactored/user-group-access-configuration/usePostUserGroupAccessMatrix';
import { usePostSearchApprovers } from '../../api-refactored/user-group-access-configuration/usePostSearchApprovers';
import { useGetRequestFileConfigs } from '../../api-refactored/user-group-access-configuration/useGetRequestFileConfigs';
import { usePostRequestFilePresigned } from '../../api-refactored/user-group-access-configuration/usePostRequestFilePresigned';
import { useGetUserGroupAuthorities } from '../../api-refactored/user-group-access-configuration/useGetUserGroupAuthorities';
import { RequestFileDropZone } from './RequestFileDropZone';
import LoadingComponent from '../LoadingComponent';

const useStyles = makeStyles()((theme) => ({
  approverRemarksField: {
    width: '63.5%',
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  backButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(1),
    },
  },
  submitButtonContainer: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(10),
  },
  errorMessage: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginBottom: theme.spacing(2),
  },
  errString: {
    color: theme.color.state.stateError,
    marginTop: theme.spacing(-2),
    marginBottom: theme.spacing(2),
  },
}));

interface EditAccessConfigurationProps {
  t: TFunction;
  userGroupUuid: string;
  functionName: string;
  showApprove?: boolean;
  showView?: boolean;
  showCreate?: boolean;
  showEdit?: boolean;
  showDelete?: boolean;
  showDataAccessLevel?: boolean;
  showUserGroupTitle?: boolean;
  onSuccessSubmitAction?: () => void;
  backButtonAction?: () => void;
}

const EditAccessConfiguration = ({
  t,
  userGroupUuid,
  functionName,
  showApprove,
  showView,
  showCreate,
  showEdit,
  showDelete,
  showDataAccessLevel,
  showUserGroupTitle = false,
  onSuccessSubmitAction,
  backButtonAction,
}: EditAccessConfigurationProps) => {
  const { classes } = useStyles();

  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [uploadStatus, setUploadStatus] = useState<MutationStatus>('idle');
  const [apiButtonStatus, setApiButtonStatus] = useState<MutationStatus>('idle');
  const [isDisabled, setIsDisabled] = useState(false);
  const [approverRequest, setApproverRequest] = useState<UserGroupApproverRequest | undefined>(
    undefined
  );
  const [groupAccessOptionsRequests, setGroupAccessOptionsRequests] = useState<
    UserGroupAuthorityRequest[]
  >([]);
  const [openTransactionLogModal, setOpenTransactionLogModal] = useState<boolean>(false);
  const [openChangeRequestSubmitModal, setOpenChangeRequestSubmitModal] = useState<boolean>(false);
  const [shouldSubmit, setShouldSubmit] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState('');

  const formHook = useForm({
    mode: 'onSubmit',
    resolver: joiResolver(editGroupAccessConfigurationSchema()),
  });
  const { handleSubmit } = formHook;

  const { mutate: mutatePostUserGroupAccessMatrix } = usePostUserGroupAccessMatrix({
    uuid: userGroupUuid,
    onSuccess: () => {
      setApiButtonStatus('success');
      setIsDisabled(false);
      setUploadStatus('success');

      setApproverRequest(undefined);
      setShouldSubmit(false);

      if (onSuccessSubmitAction) {
        onSuccessSubmitAction();
      }
    },
    onError: (error) => {
      setErrorMessage(error?.response?.data?.message!);
      setApiButtonStatus('error');
      setIsDisabled(false);
    },
  });

  const {
    mutate: mutatePostSearchApprovers,
    data: postSearchApproversData,
    isLoading: postSearchApproversIsLoading,
  } = usePostSearchApprovers({});

  const {
    data: requestFilesConfigData,
    isLoading: requestFilesConfigLoading,
  } = useGetRequestFileConfigs({});

  useEffect(() => {
    mutatePostSearchApprovers({
      functionName,
      functionModule: FunctionModule.ADMIN,
    });
  }, [functionName, mutatePostSearchApprovers]);

  const { mutate: postRequestFilePresigned } = usePostRequestFilePresigned({
    onSuccess: (data) => {
      const tempFiles: UploadedFile[] = [];
      data.forEach((_value, index) => {
        const pendingUploadFile = files[index];
        const fileData = data[index];
        pendingUploadFile.presignedUrl = fileData.presignedUrl;
        pendingUploadFile.key = fileData.key;
        tempFiles.push(pendingUploadFile);
      });
      setFiles(tempFiles);
    },
    onError: () => {
      setUploadStatus('error');
      setApiButtonStatus('error');
      setIsDisabled(false);
    },
  });

  const {
    data: groupAuthoritiesData,
    isLoading: groupAuthoritiesLoading,
    isFetching: isRefetchingGroupAuthorities,
  } = useGetUserGroupAuthorities({
    uuid: userGroupUuid,
    onSuccess: () => {
      setUploadStatus('idle');
      setApiButtonStatus('idle');
      setFiles([]);
      setErrorMessage('');
    },
  });

  useEffect(() => {
    if (!approverRequest || !shouldSubmit) {
      return;
    }

    const isAllUploaded = files.every((f) => f.uploaded);
    const fileKeys: string[] = [];
    files.forEach((f) => {
      if (f.key) {
        fileKeys.push(f.key);
      }
    });

    const fileUploadHasError = files.some((f) => f.hasError);
    if (fileUploadHasError) {
      setUploadStatus('error');
      setApiButtonStatus('error');
      setShouldSubmit(false);
    }

    const approverRequestWithFileKeys: UserGroupApproverRequest = {
      ...approverRequest,
      fileKeys,
    };
    if (approverRequest && isAllUploaded) {
      mutatePostUserGroupAccessMatrix({
        content: Object.values(groupAccessOptionsRequests),
        request: approverRequestWithFileKeys,
      });
    }
  }, [
    approverRequest,
    groupAccessOptionsRequests,
    mutatePostUserGroupAccessMatrix,
    files,
    shouldSubmit,
  ]);

  const mapApproversToDropdown = (data?: ConfigurationType[]) => {
    if (data === undefined) {
      return [];
    }

    return data?.map((a) => ({
      code: a.code,
      description: a.description,
    }));
  };

  const formOnSubmit = (e: FormEvent) => {
    e.preventDefault();

    handleSubmit((data) => {
      setOpenChangeRequestSubmitModal(true);
      setApproverRequest({ approverUuid: data.approver.code, requestorRemarks: data.remarks });
    })();
  };

  const startUploadFiles = () => {
    if (approverRequest === undefined) {
      return;
    }

    setApiButtonStatus('loading');
    setIsDisabled(true);

    const { filenames } = prepareFiles(files, setFiles);
    setUploadStatus('loading');
    if (filenames.length) {
      postRequestFilePresigned({
        filenames,
        uuid: userGroupUuid,
      });
    }
  };

  const onClickConfirm = () => {
    setIsDisabled(true);
    startUploadFiles();
    setShouldSubmit(true);
    setErrorMessage('');
    setOpenChangeRequestSubmitModal(false);
  };

  if (
    groupAuthoritiesLoading ||
    postSearchApproversIsLoading ||
    requestFilesConfigLoading ||
    isRefetchingGroupAuthorities
  ) {
    return <LoadingComponent isOpaque={false} />;
  }

  return (
    <>
      {showUserGroupTitle && (
        <TitleHeader title={groupAuthoritiesData?.userGroupDisplayName || ''} />
      )}
      <form onSubmit={formOnSubmit}>
        <ContentTemplate type="full">
          {userGroupUuid !== '-' && (
            <>
              <UserGroupFunctionPermissionAccessOptionsDataTable
                data={groupAuthoritiesData?.content || []}
                editable={!isDisabled}
                setGroupAccessOptionsRequests={setGroupAccessOptionsRequests}
                showApprove={showApprove}
                showView={showView}
                showCreate={showCreate}
                showEdit={showEdit}
                showDelete={showDelete}
                showDataAccessLevel={showDataAccessLevel}
              />

              <Divider paddingTop />

              <ChangeRequestForm
                formHook={formHook}
                approverDropdowns={mapApproversToDropdown(postSearchApproversData)}
                isEditable={!isDisabled}
              />

              {requestFilesConfigData && (
                <Grid container spacing={0}>
                  <Grid md={6} sm={12}>
                    <RequestFileDropZone
                      fileConfig={requestFilesConfigData}
                      files={files}
                      setFiles={setFiles}
                      isLoading={uploadStatus === 'loading'}
                    />
                  </Grid>
                </Grid>
              )}
            </>
          )}

          <TransactionAuditLogUpdatedDetails
            updatedAt={groupAuthoritiesData?.updatedAt as string}
            updatedBy={groupAuthoritiesData?.updatedBy as string}
            handleOpenLogModal={() => setOpenTransactionLogModal(true)}
            label={t('admin->>UserGroups.ViewTransactionLog')}
          />

          <Box className={classes.submitButtonContainer}>
            {errorMessage && (
              <Typography className={classes.errorMessage} variant="body2" color="error">
                {errorMessage}
              </Typography>
            )}
            {backButtonAction && (
              <Button
                className={classes.backButton}
                id="button-create-new-announcement-cancel"
                variant="outlined"
                disabled={isDisabled}
                onClick={() => backButtonAction()}
              >
                {t('admin->>UserGroups.Back').toString()}
              </Button>
            )}
            <ApiButton
              status={apiButtonStatus}
              id="button-update-user-group-access-matrix"
              type="submit"
            >
              {t('admin->>UserGroups.ConfirmChanges').toString()}
            </ApiButton>
          </Box>
        </ContentTemplate>
        {openTransactionLogModal && (
          <TransactionLogModal
            modalState={openTransactionLogModal}
            setOpenModalState={setOpenTransactionLogModal}
            requestType={REQUEST_TYPES.ADMIN_ACCESS_CONFIG}
          />
        )}
        <ChangeRequestDialog
          open={openChangeRequestSubmitModal}
          onClose={() => setOpenChangeRequestSubmitModal(false)}
          onConfirm={onClickConfirm}
        />
      </form>
    </>
  );
};

export default EditAccessConfiguration;
